import { MatcherDsl, Namespace } from '../types/rest/dslTypes';
import { allMatcherOperators } from '../views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import { Base64 } from 'js-base64';
import _ from 'lodash';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import i18n from 'i18n';
import { BodyMatcherItem } from '../components/transactionEditor/BodyMatchersList';
import React from 'react';
import CheckBox from '../components/ui/checkBox/CheckBox';
import { DelayType, ResponseDelay } from '../types/rest/mockServiceTypes';
import { decodeBase64 } from './stringUtils';

export interface MatcherBody {
  id: string;
  matcher: string;
  content: string;
  namespaces: any[];
  isRequired: boolean;
  cdataXpath: string;
}

export const buildBody = (body: MatcherDsl[]) => {
  return body.map(
    (bm, ix): MatcherBody => ({
      id: 'body-matchers-' + ix,
      matcher: allMatcherOperators[bm.matcherName],
      content: bm.matchingValue ? Base64.decode(bm.matchingValue) : '',
      namespaces: bm.namespaces ? buildNameSpaces(bm.namespaces) : [],
      isRequired: !_.isNil(bm.optional) ? !bm.optional : false,
      cdataXpath: !_.isNil(bm.cdataXpath) ? Base64.decode(bm.cdataXpath) : null,
    }),
  );
};

const buildNameSpaces = (namespaces: Namespace[]) => {
  return namespaces.map(nsp => ({
    prefix: nsp.prefix ? decodeBase64(nsp.prefix) : '',
    uri: nsp.uri ? decodeBase64(nsp.uri).toString() : '',
  }));
};

const isRequiredAttributeShown = (item: BodyMatcherItem) => {
  const isCdataMatcher = item.matcher === i18n.t('transactions:editor.matchesCdataXpath');
  const isXpathMatcher = item.matcher === i18n.t('transactions:editor.matchesXpath');
  const isJsonPathMatcher = item.matcher === i18n.t('transactions:editor.matchesJsonPath');
  return isCdataMatcher || isXpathMatcher || isJsonPathMatcher;
};

export const getBodyMatchers = (matcherBodies: MatcherBody[]) => {
  return matcherBodies.map(item => {
    const xmlNamespaces: string = item.namespaces.map(np => ' ' + np.prefix + '=' + np.uri).toString();
    return (
      <div id={'xpathBodyDiv-' + item.id} key={item.id} className={'mb-5'}>
        <div className="mb-3">
          <Form.Label>{i18n.t('transactions:editor.bodyMatcherLabel')}</Form.Label>
          <Form.Control
            name="bodyMatch"
            readOnly={true}
            placeholder={i18n.t('transactions:editor.bodyMatcherLabel')}
            value={item.matcher}
          />
        </div>

        {/* XML Namespaces */}
        {xmlNamespaces && item.matcher === i18n.t('transactions:editor.matchesXpath') && (
          <div id={'namespacedDiv'} className={'mt-4 mb-4'}>
            <Form.Label className={'mb-0'}>{i18n.t('transactions:editor.xmlNamespaces')} </Form.Label>
            <div className={'mt-1'}>{xmlNamespaces}</div>
          </div>
        )}

        {/* Body Matcher Content */}
        <div className="my-3">
          <Form.Label>{i18n.t('transactions:editor.bodyMatcherContent')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            name="content"
            placeholder={i18n.t('transactions:editor.bodyMatcherContent')}
            value={item.content}
            readOnly={true}
          />
        </div>

        {/*XPath of the CData element*/}
        {item.matcher === i18n.t('transactions:editor.matchesCdataXpath') && (
          <div className="my-3">
            <Form.Label>{i18n.t('transactions:editor.cdataXpath')}</Form.Label>
            <Form.Control
              name="xpath"
              placeholder={i18n.t('transactions:editor.bodyMatcherContent')}
              value={item.cdataXpath}
              readOnly={true}
            />
          </div>
        )}

        {/* Required Attribute */}
        {isRequiredAttributeShown(item as BodyMatcherItem) && (
          <CheckBox label={'Required'} title={'Required'} disabled={true} onCheck={_.noop} checked={item.isRequired} />
        )}
        <hr />
      </div>
    );
  });
};

export const getThinkTimeDom = (responseDelay: ResponseDelay) => {
  switch (responseDelay?.type) {
    case DelayType.FIXED:
      return (
        <>
          <Form.Label>{i18n.t('common:thinkTime.fixedDelay')}</Form.Label>
          <Row>
            <Col md={3}>
              <InputGroup className="mb-1">
                <Form.Control value={responseDelay?.duration.toString()} readOnly={true} />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </Col>
          </Row>
        </>
      );
    case DelayType.UNIFORM:
      return (
        <>
          <Form.Label>{i18n.t('common:thinkTime.randomUniform')}</Form.Label>
          <Row>
            <Col md={3}>
              <InputGroup className="mb-1">
                <Form.Control value={responseDelay?.lower.toString()} readOnly={true} />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.uniformLower') }}
              ></p>
            </Col>
            <Col md={3}>
              <InputGroup className="mb-1">
                <Form.Control value={responseDelay?.upper.toString()} readOnly={true} />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.uniformUpper') }}
              ></p>
            </Col>
          </Row>
        </>
      );
    case DelayType.LOGNORMAL:
      return (
        <>
          <Form.Label>{i18n.t('common:thinkTime.randomLognormal')}</Form.Label>
          <Row>
            <Col md={3}>
              <InputGroup className="mb-1">
                <Form.Control value={responseDelay?.median.toString()} readOnly={true} />
                <InputGroup.Append>
                  <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.lognormalMedian') }}
              ></p>
            </Col>
            <Col md={3}>
              <InputGroup className="mb-1">
                <Form.Control value={responseDelay?.sigma.toString()} readOnly={true} />
              </InputGroup>
              <p
                className="text-muted small"
                dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.lognormalSigma') }}
              ></p>
            </Col>
          </Row>
        </>
      );
    default:
      return <></>;
  }
};

export const getDetailTitle = (title: string, param: string | number | null) => (
  <>
    {title}
    {_.isNull(param) ? '' : <span className="text-muted"> ({param})</span>}
  </>
);

export const isThinkTimeSet = (responseDelay: ResponseDelay): boolean => {
  return !_.isNull(responseDelay) && responseDelay?.type !== DelayType.NONE;
};
